import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversionRateOverviewResponse } from '../../../../../hooks';
import { Icons } from '../../../../../stories/atoms';
import { areaOverviewTranslations } from '../../../../../translations/areaOverview';
import { formatAmountWithSeparator } from '../../../../../utils';
import { OverviewWidget } from '../../../../areaOverview/overviewWidget/OverviewWidget';
import { PerformanceDrawerContent } from '../../../../areaOverview/overviewWidget/performanceDrawerContent/PerformanceDrawerContent';
import { AreaWidgetType } from '../../../../areaOverview/util';

type ConversionRateOverviewWidgetProps = {
  data: ConversionRateOverviewResponse | null;
  isLoading: boolean;
  isError: boolean;
  errorRetryHandler: () => void;
};

export const ConversionRateOverviewWidget: FC<ConversionRateOverviewWidgetProps> = observer(
  ({ data, isLoading, isError, errorRetryHandler }) => {
    const { t } = useTranslation();

    const conversionRate = data?.totalVisitors ? (data.totalTransactions / data.totalVisitors) * 100 : 0;
    const conversionRateLW = data?.totalVisitorsLW ? (data.totalTransactionsLW / data.totalVisitorsLW) * 100 : 0;
    const conversionRateLY = data?.totalVisitorsLY ? (data.totalTransactionsLY / data.totalVisitorsLY) * 100 : 0;

    const { conversionRateOverview, performance } = areaOverviewTranslations;

    const sliderContent = data ? (
      <PerformanceDrawerContent
        title={t(performance.drawerTitle.key, performance.drawerTitle.defaultValue)}
        items={[
          {
            label: t(conversionRateOverview.headerText.key, conversionRateOverview.headerText.defaultValue),
            currentValue: conversionRate,
            currentValueFormatted: formatAmountWithSeparator(conversionRate, '%'),
            lastWeek: conversionRateLW,
            lastWeekFormatted: formatAmountWithSeparator(conversionRateLW, '%'),
            lastYear: conversionRateLY,
            lastYearFormatted: formatAmountWithSeparator(conversionRateLY, '%'),
            isEffectiveHours: false,
          },
          {
            label: t(
              conversionRateOverview.totalVisitorsText.key,
              conversionRateOverview.totalVisitorsText.defaultValue,
            ),
            currentValue: data.totalVisitors,
            currentValueFormatted: formatAmountWithSeparator(data.totalVisitors),
            lastWeek: data.totalVisitorsLW,
            lastWeekFormatted: formatAmountWithSeparator(data.totalVisitorsLW),
            lastYear: data.totalVisitorsLY,
            lastYearFormatted: formatAmountWithSeparator(data.totalVisitorsLY),
            isEffectiveHours: false,
          },
        ]}
      />
    ) : undefined;

    return (
      <OverviewWidget
        id={AreaWidgetType.ConversionRateOverview}
        isLoading={isLoading}
        isError={isError}
        errorRetryHandler={errorRetryHandler}
        title={t(conversionRateOverview.title.key, conversionRateOverview.title.defaultValue)}
        headerIcon={Icons.CONVERSION}
        headerText={t(conversionRateOverview.headerText.key, conversionRateOverview.headerText.defaultValue)}
        headerValue={formatAmountWithSeparator(conversionRate, '%')}
        headerComparison={
          data
            ? {
                current: conversionRate,
                lastWeek: conversionRateLW,
                lastWeekFormatted: formatAmountWithSeparator(conversionRateLW, '%'),
                lastYear: conversionRateLY,
                lastYearFormatted: formatAmountWithSeparator(conversionRateLY, '%'),
                isEffectiveHours: false,
              }
            : undefined
        }
        widgetText={t(conversionRateOverview.widgetText.key, conversionRateOverview.widgetText.defaultValue)}
        statistics={[
          {
            label: t(
              conversionRateOverview.utilisedTillsText.key,
              conversionRateOverview.utilisedTillsText.defaultValue,
            ),
            value: formatAmountWithSeparator(data?.totalTills ?? 0),
          },
          {
            label: t(
              conversionRateOverview.totalVisitorsText.key,
              conversionRateOverview.totalVisitorsText.defaultValue,
            ),
            value: formatAmountWithSeparator(data?.totalVisitors ?? 0),
            comparison: data
              ? {
                  current: data.totalVisitors,
                  lastWeek: data.totalVisitorsLW,
                  lastWeekFormatted: formatAmountWithSeparator(data.totalVisitorsLW),
                  lastYear: data.totalVisitorsLY,
                  lastYearFormatted: formatAmountWithSeparator(data.totalVisitorsLY),
                  isEffectiveHours: false,
                }
              : undefined,
          },
        ]}
        sliderContent={sliderContent}
      />
    );
  },
);
